import { FC, useState, useEffect } from "react";
import "./Use.scss";
import MainNavbar from "../../../components/MainNavbar/MainNavbar";
import ProfileLayout from "../../../components/ProfileLayout/ProfileLayout";
import { useUse } from "../../../providers/Profile/Use/UseProvider";
import { BvIcon } from "bevi-icon";
import Swal from "sweetalert2";
import { renderToStaticMarkup } from "react-dom/server";
import { useAuth } from "../../../providers/AuthProvider";
import { useNavigate } from "react-router-dom";
import { UserRoleEnum } from "../../../enums/User/UserRoleEnum";
import { toast } from "react-toastify";

const Use: FC = () => {
  const { availableUsers, dependents, messageBalance, userQuantity, addUserToPlan, toggleUserRemoval, refreshDependents } = useUse();
  const [planUserIds, setPlanUserIds] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const totalMessages = messageBalance?.total_messages ?? 0;
  const messagesUsed = messageBalance?.messages_used ?? 0;
  const usagePercent = totalMessages > 0 ? (messagesUsed / totalMessages) * 100 : 0;

  const { userLogged, profileImageUrl } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (userLogged && userLogged.role !== UserRoleEnum.ADMIN) {
      navigate("/", { replace: true });
    }
  }, [userLogged]);

  const filteredAvailable = availableUsers.filter((u: any) =>
    u.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenModal = () => {
    setSelectedIds([...planUserIds]);
    setSearch("");
    setShowModal(true);
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };


  const handleRemoveUser = (userId: number, pendingRemoval: boolean) => {
    Swal.fire({
      title: pendingRemoval ? "Cancelar remoção" : "Remover usuário",
      text: pendingRemoval
        ? "Deseja cancelar a remoção deste usuário do plano?"
        : "Deseja remover este usuário do plano?",
      iconHtml: renderToStaticMarkup(<BvIcon name="warning" variant="solid" width={106} height={106} style={{ color: "#787ABA" }} />),
      showCancelButton: true,
      confirmButtonColor: "#222343",
      cancelButtonColor: "#6c757d",
      confirmButtonText: pendingRemoval ? "Sim, quero cancelar" : "Sim, quero remover",
      cancelButtonText: "Não, cancelar",
      reverseButtons: true,
      customClass: {
        title: "swal-title",
        htmlContainer: "swal-text",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        toggleUserRemoval(userId, pendingRemoval);
      }
    });
  };

  const handleConcluir = async () => {
    const newUsers = selectedIds.filter((id) => !planUserIds.includes(id));
    const totalAfterAdd = dependents.length + newUsers.length;

    setShowModal(false);

    if (userQuantity !== null && totalAfterAdd > userQuantity) {
      toast.error(`Limite de usuários atingido`);
      return;
    }

    await Promise.all(newUsers.map((id) => addUserToPlan(id)));
    setPlanUserIds([...selectedIds]);
    await refreshDependents();
  };

  return (
    <>
      <MainNavbar />
      <div className="Use" data-testid="Use">
        <div className="usage-card">
          <ProfileLayout profileImageUrl={profileImageUrl}>
            <div className="usage-content-inner">
              <h5 className="usage-title">Uso</h5>

              <div className="usage-section-header">
                <div>
                  <p className="usage-section-title">Mensagens utilizadas</p>
                  <p className="usage-section-subtitle">
                    Gerencie usuários e mensagens utilizadas
                  </p>
                </div>
                {messageBalance && (
                  <button className="usage-add-btn" onClick={handleOpenModal}>
                    Adicionar Usuário
                  </button>
                )}
              </div>

              <div className="usage-progress-row">
                <span className="usage-progress-label">
                  0 a {totalMessages}
                  <br />
                  mensagens
                </span>
                <div className="usage-progress-bar-wrapper">
                  <div
                    className="usage-progress-bar-fill"
                    style={{ width: `${usagePercent}%` }}
                  />
                </div>
                <span className="usage-progress-count">
                  {messagesUsed}{" "}
                  <span className="usage-progress-count-label">utilizadas</span>
                </span>
              </div>

              {messageBalance && (
                <div className="usage-table-wrapper">
                  <table className="usage-table">
                    <thead>
                      <tr>
                        <th>Usuário</th>
                        <th>Código Parceiro</th>
                        <th>Mensagens</th>
                        <th>Remover usuário</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dependents.map((dep: any) => (
                        <tr key={dep.id}>
                          <td>{dep.name}</td>
                          <td>{dep.partner_code ?? "-"}</td>
                          <td>{dep.messages_count ?? 0}</td>
                          <td>
                            <button className="usage-remove-btn" onClick={() => handleRemoveUser(dep.id, dep.pending_removal)}>
                              {dep.pending_removal ? "Cancelar" : "Remover"}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {Array.from({ length: Math.max(0, (userQuantity ?? 0) - dependents.length) }).map((_, i) => (
                        <tr key={`empty-${i}`} className="usage-row-empty">
                          <td>Usuário Disponível</td>
                          <td>-</td>
                          <td>0</td>
                          <td>-</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}  
            </div>
          </ProfileLayout>
        </div>
      </div>

      {showModal && (
        <div className="usage-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="usage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="usage-modal-header">
              <div className="usage-modal-header-left">
                <BvIcon name="personAdd" style={{color: "#fff"}} />
                <span className="usage-modal-title">Adicionar Usuário</span>
              </div>
              <button
                className="usage-modal-close"
                onClick={() => setShowModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="usage-modal-body">
              <div className="usage-modal-search-wrapper">
                <input
                  type="text"
                  className="usage-modal-search"
                  placeholder="Busque aqui..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <span className="usage-modal-search-icon">
              <BvIcon style={{ width: '24px', height: '24px', color: '#858693' }} name="search" />
                </span>
              </div>

              <div className="usage-modal-list">
                {filteredAvailable.map((user) => {
                  const isSelected = selectedIds.includes(user.id);
                  return (
                    <div
                      key={user.id}
                      className={`usage-modal-item ${isSelected ? "selected" : ""}`}
                      onClick={() => toggleSelect(user.id)}
                    >
                      <div className="usage-modal-checkbox">
                        {isSelected && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path
                              d="M2 6L5 9L10 3"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="usage-modal-user-name">{user.name}</span>
                      <span className="usage-modal-partner-code">{user.code}</span>
                      <span className="usage-modal-role">{user.tipo}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="usage-modal-footer">
              <button className="usage-modal-confirm" onClick={handleConcluir}>
                Concluir
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Use;
